import React, { useState, useEffect } from 'react';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Rebrand Announcement Banner
 * Info tip banner announcing Burnt → Hylo rebrand
 * Matches the style of ForumRules component
 */
const RebrandAnnouncementBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem('rebrandAnnouncementDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('rebrandAnnouncementDismissed', 'true');
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 shadow-sm">
      <div className="px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-start">
          {/* Icon */}
          <div className="flex-shrink-0">
            <SparklesIcon 
              className="h-6 w-6 sm:h-7 sm:w-7 text-purple-600 dark:text-purple-400" 
              aria-hidden="true" 
            />
          </div>

          {/* Content */}
          <div className="ml-3 flex-1">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-2">
              An Exciting New Chapter: Burnt is Now Hylo
            </h3>
            <div className="text-sm sm:text-base text-gray-700 dark:text-gray-300 space-y-2">
              <p className="leading-relaxed">
                Thank you for being one of our foundational members. Your early support and invaluable feedback have helped us build the next generation of professional networking.
              </p>
              <p className="leading-relaxed">
                We're thrilled to announce that Burnt is rebranding to{' '}
                <span className="font-semibold text-purple-700 dark:text-purple-300">Hylo: The High-Signal, Low-Noise Professional Network.</span>
              </p>
              <p className="leading-relaxed">
                We are in the process of rolling out this new and improved product. To make this transition seamless, your account and all your data will be automatically migrated to the new Hylo platform.
              </p>
              <p className="leading-relaxed">
                <span className="font-medium text-purple-700 dark:text-purple-300">There is no action needed from you.</span>
                {' '}We'll be in touch with more details soon and will notify you the moment your new Hylo experience is ready.
              </p>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                Questions? Reach out to{' '}
                <a 
                  href="mailto:arjan@justburnt.com" 
                  className="text-purple-600 dark:text-purple-400 hover:underline font-medium"
                >
                  arjan@justburnt.com
                </a>
              </p>
            </div>
          </div>

          {/* Dismiss Button */}
          <div className="ml-3 flex-shrink-0">
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex rounded-md p-1.5 text-purple-600 hover:bg-purple-100 dark:text-purple-400 dark:hover:bg-purple-900/30 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors duration-200"
              aria-label="Dismiss rebrand announcement"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Decorative gradient border on the left */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500 to-indigo-500"></div>
    </div>
  );
};

export default RebrandAnnouncementBanner;
